import React from 'react';
import { graphql } from '../../../../generated';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { FormForm } from '../../../../components/Form/FormForm';
import { t } from '../../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError, handleSuccess } from '../../../../model/notification';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { FormCheckbox } from '../../../../components/Form/Checkbox/FormCheckbox';
import { Label } from '../../../../components/Form/Label/Label';
import { Stack } from '@nerdgeschoss/shimmer-component-stack';

const ORDER_PAYMENT_DETAILS = graphql(`
  query GetOrderPaymentDetails($id: ID!) {
    order(id: $id) {
      id
      payments {
        nodes {
          bankAccount {
            name
            iban
            bic
            bankName
          }
        }
      }
    }
  }
`);

const OFFER_FOR_RESALE = graphql(`
  mutation OfferForResale($itemIds: [ID!]!, $public: Boolean!, $bankAccount: BankAccountInput) {
    offerForResale(input: { itemIds: $itemIds, public: $public, bankAccount: $bankAccount }) {
      clientMutationId
    }
  }
`);

interface Props {
  id: string;
  itemIds: string[];
  onSuccess: () => void;
  isBankInformationRequired?: boolean;
}

export function OfferForResale({ id, itemIds, isBankInformationRequired, onSuccess }: Props): JSX.Element {
  const { data } = useSuspenseQuery(ORDER_PAYMENT_DETAILS, { variables: { id }, skip: !isBankInformationRequired });
  const { open, Modal, size, close } = useModal();
  const paymentDetails = data?.order ? data.order.payments.nodes.find((p) => p.bankAccount) : null;

  const [offerForResale] = useMutation(OFFER_FOR_RESALE);

  const { fields, valid, onSubmit, submissionStatus } = useForm<{
    iban: string;
    name: string;
    bankName: string;
    bic: string;
    public?: boolean;
  }>({
    model: {
      iban: paymentDetails?.bankAccount?.iban ?? '',
      name: paymentDetails?.bankAccount?.name ?? '',
      bankName: paymentDetails?.bankAccount?.bankName ?? '',
      bic: paymentDetails?.bankAccount?.bic ?? '',
      public: false,
    },
    onSubmit: async ({ model }) => {
      await offerForResale({
        variables: {
          itemIds,
          public: !!model.public,
          bankAccount: isBankInformationRequired
            ? {
                iban: model.iban,
                name: model.name,
                bankName: model.bankName,
                bic: model.bic,
              }
            : undefined,
        },
      });

      handleSuccess(
        itemIds.length > 1 ? t.model.order.offerForResale.successTickets : t.model.order.offerForResale.successTicket
      );
      onSuccess();
      close();
    },
    validations: {
      iban: [
        ({ model }) => {
          if (isBankInformationRequired && !model.iban) {
            return ['required'];
          }
          return [];
        },
      ],
      name: [
        ({ model }) => {
          if (isBankInformationRequired && !model.name) {
            return ['required'];
          }
          return [];
        },
      ],
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={t.model.order.offerForResale.title} size={size}>
      <FormForm onSubmit={onSubmit} onClose={close} canSubmit={valid} loading={submissionStatus === 'submitting'}>
        <Stack gap={20}>
          {isBankInformationRequired && (
            <Stack gap={4}>
              <Label title={t.model.order.offerForResale.bankDetails} />
              <FormTextField {...fields.name} label={t.model.order.offerForResale.recipient} required />
              <Stack line gap={32}>
                <FormTextField {...fields.iban} label={t.model.order.offerForResale.iban} required />
                <FormTextField {...fields.bic} label={t.model.order.offerForResale.bic} />
              </Stack>
              <FormTextField {...fields.bankName} label={t.model.order.offerForResale.bankName} />
            </Stack>
          )}
          <FormCheckbox {...fields.public} label={t.model.order.offerForResale.public} />
        </Stack>
      </FormForm>
    </Modal>
  );
}
